import type { ReactNode } from "react"
import { useEffect, useLayoutEffect, useMemo, useState } from "react"
import { useConnectionState } from "@rocicorp/zero/react"

import { cn } from "@productlane/lib/src/cn"
import { Button } from "@productlane/ui"

import { sanitizeMessageHtml } from "@/lib/sanitize-message-html"
import { useSession } from "@/lib/session"
import { useUnsentMessages, visibleUnsent } from "@/lib/unsent-messages"
import { SlackStyleMessage } from "./base/slack-style-message"

const REPLAY_GRACE_MS = 3000
export const ENTER_MS = 200

export const ENTER_FROM_CLASS = "translate-y-1 opacity-0 transition-none"
export const ENTERING_CLASS =
	"transition-[opacity,transform] duration-200 [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none"

// The optimistic row and the real row share an id; only the first mount animates.
const entered = new Set<string>()

type EnterPhase = "idle" | "from" | "entering"

/** Fades a reply in once, the moment the sender posts it. */
export function MessageEnter({
	id,
	children,
}: {
	id: string
	children: ReactNode
}) {
	const [phase, setPhase] = useState<EnterPhase>(() => {
		if (entered.has(id)) return "idle"
		const sending =
			useUnsentMessages.getState().messages[id]?.status === "sending"
		if (!sending) return "idle"
		entered.add(id)
		return "from"
	})

	useLayoutEffect(() => {
		if (phase !== "from") return
		// Commit the start pose before paint so the class swap transitions from it.
		document.body.getBoundingClientRect()
		setPhase("entering")
	}, [phase])

	useEffect(() => {
		if (phase !== "entering") return
		const timer = setTimeout(() => setPhase("idle"), ENTER_MS)
		return () => clearTimeout(timer)
	}, [phase])

	return (
		<div
			className={cn(
				phase === "from" && ENTER_FROM_CLASS,
				phase === "entering" && ENTERING_CLASS,
			)}
		>
			{children}
		</div>
	)
}

/** Replies that have no timeline row yet: still sending, or not sent. */
export function UnsentMessages({
	threadId,
	renderedIds,
}: {
	threadId: string
	renderedIds: ReadonlySet<string>
}) {
	const { session } = useSession()
	const messages = useUnsentMessages((state) => state.messages)
	const retries = useUnsentMessages((state) => state.retries)
	const settle = useUnsentMessages((state) => state.settle)
	const settleQueued = useUnsentMessages((state) => state.settleQueued)
	const connected = useConnectionState().name === "connected"
	const queuedCount = useUnsentMessages(
		(state) => Object.keys(state.queued).length,
	)

	useEffect(() => {
		if (!connected || queuedCount === 0) return
		const timer = setTimeout(settleQueued, REPLAY_GRACE_MS)
		return () => clearTimeout(timer)
	}, [connected, queuedCount, settleQueued])
	const visible = useMemo(
		() => visibleUnsent(Object.values(messages), threadId, renderedIds),
		[messages, threadId, renderedIds],
	)

	if (visible.length === 0) return null

	return (
		<div className="w-full">
			{visible.map((message) => {
				const failed = message.status === "failed"
				return (
					<MessageEnter key={message.id} id={message.id}>
						<div data-testid="unsent-message">
							<SlackStyleMessage
								showAvatar
								senderName={session?.name}
								senderImage={session?.imageUrl ?? undefined}
								timestamp={new Date(message.createdAt)}
								isOutbound
							>
								<div
									className="html-content"
									dangerouslySetInnerHTML={{
										__html: sanitizeMessageHtml(message.html),
									}}
								/>
							</SlackStyleMessage>
							{failed && (
								<div className="-mt-1 mb-3 flex items-center gap-1 pl-11 text-xs text-secondary">
									<span>Not sent.</span>
									<Button
										variant="link"
										size="xs"
										onClick={() => retries[message.id]?.()}
									>
										Retry
									</Button>
									<span aria-hidden>·</span>
									<Button
										variant="link"
										size="xs"
										onClick={() => settle(message.id)}
									>
										Delete
									</Button>
								</div>
							)}
						</div>
					</MessageEnter>
				)
			})}
		</div>
	)
}
